import React from 'react';

import { closeAllGenericDrawer, closeGenericDrawer, openGenericDrawer } from 'actions/genericDrawer';
import {
  closeObjectiveCreationModalAction,
  openObjectiveCreationModalAction,
} from 'actions/objectiveCreationModalActions';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import msTeamsContext from 'reducers/msTeamsContext';
import ObjectiveSelectionDrawerComp from 'screens/Gmail/src/components/GoalList/ObjectiveSelectionDrawerComp';
import { selectIsGmailOutlookApp } from 'selectors/gmailOutlookApp';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledBox, StyledTooltip } from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { PlusIcon2, StyledIconV2 } from 'constants/icons';

import { Typography13, Typography14 } from 'uiToolkit/Typography';

export default function GoalObjectiveField({
  taskDetails,
  updateTask,
  isRealign = false,
  children,
  parentPageSelectedTimeBoundPeriod = null,
}) {
  const dispatch = useDispatch();
  const isGmailOutlookApp = useSelector(selectIsGmailOutlookApp);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const selectedObjective = taskDetails?.subObjective;

  const enableCreation =
    msTeamsContext && Object.keys(msTeamsContext).length > 0
      ? false
      : checkUserIsGoalAdmin() || loggedInEmployee.isHRISManager;

  const closePopover = () => {
    dispatch(closeObjectiveCreationModalAction());
  };

  // when no objective is selected and user navigates back one level using back button
  const closeDrawer = (level) => {
    dispatch(closeGenericDrawer({ level }));
  };

  // in case of selection in drawer view we need to close all nested drawer together on selection of objective
  const closeAllDrawer = () => {
    dispatch(closeAllGenericDrawer());
  };

  const handleSelectTag = (tag) => {
    updateTask({ ...taskDetails, subObjective: tag });
    isGmailOutlookApp ? closeAllDrawer() : closePopover();
  };

  const openObjectiveModal = () => {
    dispatch(
      openObjectiveCreationModalAction({
        onClose: closePopover,
        onSelect: handleSelectTag,
        enableCreation,
        selectedObjective,
        selectedTimeBoundPeriod: selectedObjective?.timeBoundDetails,
      })
    );
  };

  // in case of outlook/gmail app
  const openObjectiveDrawer = () => {
    dispatch(
      openGenericDrawer({
        contentComponent: (
          <ObjectiveSelectionDrawerComp onClose={closeDrawer} onSelect={handleSelectTag} drawerLevel={1} />
        ),
        level: 1,
      })
    );
  };

  if (selectedObjective) {
    return (
      <StyledTooltip title={isRealign ? '' : 'Edit Objective'}>
        <HoverableStyledBox
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          onClick={() => {
            isGmailOutlookApp
              ? openObjectiveDrawer()
              : dispatch(
                  openRealignModalAction({
                    objectiveId: selectedObjective?.objectiveId,
                    realignId: selectedObjective?.objectiveTimeBoundId,
                    objectiveName: selectedObjective?.displayName,
                    selectedTimeBoundPeriod: selectedObjective?.timeBoundDetails,
                    selectedObjective: {
                      timeBoundDetails: selectedObjective?.timeBoundDetails,
                      objectiveId: selectedObjective?.objectiveId,
                    },
                    onSelect: (tag) => {
                      // updateTask({ ...taskDetails, subObjective: tag });
                      updateTask({
                        ...taskDetails,
                        subObjective: {
                          ...tag,
                          objectiveType: 'GOAL',
                        },
                      });
                    },
                    isSelectObjectiveModal: true,
                  })
                );
          }}
          style={{ cursor: 'pointer', marginLeft: isRealign ? 'auto' : '-1rem' }}
        >
          {isRealign ? (
            children
          ) : (
            <>
              <TagIcon tagId={selectedObjective?.objectiveLevel} />

              <Typography14 style={{ lineHeight: 1, marginBottom: '2px' }} leftGutter={1.5}>
                {selectedObjective?.name}
              </Typography14>
            </>
          )}
        </HoverableStyledBox>
      </StyledTooltip>
    );
  }

  return (
    <HoverableStyledBox
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      onClick={() => {
        isGmailOutlookApp
          ? openObjectiveDrawer()
          : dispatch(
              openRealignModalAction({
                objectiveId: selectedObjective?.objectiveId,
                realignId: selectedObjective?.objectiveTimeBoundId,
                objectiveName: selectedObjective?.displayName,
                selectedTimeBoundPeriod: selectedObjective
                  ? selectedObjective.timeBoundDetails
                  : (parentPageSelectedTimeBoundPeriod ?? null),
                selectedObjective: {
                  timeBoundDetails: selectedObjective?.timeBoundDetails,
                  objectiveId: selectedObjective?.objectiveId,
                },
                onSelect: (tag) => {
                  // updateTask({ ...taskDetails, subObjective: tag });
                  updateTask({
                    ...taskDetails,
                    subObjective: {
                      ...tag,
                      objectiveType: 'GOAL',
                    },
                  });
                },
                isSelectObjectiveModal: true,
              })
            );
      }}
      style={{ cursor: 'pointer' }}
    >
      <StyledIconV2 size="fixed10px" containerSize="fixed24px" circularOutline color="primary" rightGutter={2}>
        <PlusIcon2 />
      </StyledIconV2>
      <Typography13 color="primary">{`Select ${objectivesName}*`}</Typography13>
    </HoverableStyledBox>
  );
}

const HoverableStyledBox = styled(StyledBox)`
  border-radius: 4px;

  margin: -1rem;
  padding: 0.75rem 1rem;

  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
`;
